import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { shootCoinsFrom, playCoins, playExplosion, playCelebration, playBellDing } from "@/lib/confetti";
import CoinStreakBanner from "@/components/CoinStreakBanner";

function animateNumber(set, from, to, ms) {
  const start = performance.now();
  const tick = (now) => {
    const p = Math.min(1, (now - start) / ms);
    set(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function Rocket() {
  return (
    <svg width="70" height="130" viewBox="0 0 70 130" style={{ overflow: "visible" }}>
      <path d="M35 2 q20 24 20 62 l-40 0 q0 -38 20 -62z" fill="#ECEFF1" stroke="#111" strokeWidth="2" />
      <path d="M35 2 q10 12 14 30 l-28 0 q4 -18 14 -30z" fill="#E53935" stroke="#111" strokeWidth="1.6" />
      <circle cx="35" cy="52" r="10" fill="#00E5FF" stroke="#111" strokeWidth="2.4" />
      <path d="M15 64 l-14 26 14 -6z M55 64 l14 26 -14 -6z" fill="#E53935" stroke="#111" strokeWidth="1.6" />
      <rect x="15" y="64" width="40" height="14" rx="4" fill="#B0BEC5" stroke="#111" strokeWidth="1.6" />
      <path d="M25 78 l20 0 -4 10 -12 0z" fill="#546E7A" stroke="#111" strokeWidth="1.4" />
    </svg>
  );
}

// Rocket Launch: countdown 3-2-1, ignition shakes the pad, the rocket lifts off
// riding a column of gold coins, and the totals appear in the smoke trail.
export default function RocketLaunchCelebration({
  coinsAwarded, totalCoins, scorePercent,
  canAdvance, onRetry, onContinue, soundOn = true, hasPrev = false, onPrev, onDone,
}) {
  const prevTotal = Math.max(0, (totalCoins || 0) - (coinsAwarded || 0));
  const [phase, setPhase] = useState("count"); // count -> ignite -> liftoff -> orbit -> done
  const [tick, setTick] = useState(3);
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(prevTotal);
  const timers = useRef([]);
  const sceneRef = useRef(null);
  const showScore = ["liftoff", "orbit", "done"].includes(phase);

  const burst = (xr, yr) => {
    const r = sceneRef.current?.getBoundingClientRect();
    if (!r) return;
    shootCoinsFrom((r.left + r.width * xr) / window.innerWidth, (r.top + r.height * yr) / window.innerHeight);
  };

  useEffect(() => {
    const add = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    add(() => { setTick(2); if (soundOn) playBellDing(1); }, 900);
    add(() => { setTick(1); if (soundOn) playBellDing(1); }, 1800);
    add(() => { setPhase("ignite"); if (soundOn) playCelebration(); }, 2700);
    add(() => {
      setPhase("liftoff");
      if (soundOn) playExplosion();
      animateNumber(setCount, 0, coinsAwarded, 1400);
      animateNumber(setTotal, prevTotal, totalCoins, 1600);
      // the coin exhaust column
      for (let i = 0; i < 7; i++) add(() => { burst(0.5, 0.72 - i * 0.02); if (soundOn && i % 2 === 0) playCoins(5); }, 150 + i * 380);
    }, 3600);
    add(() => { setPhase("orbit"); if (soundOn) playCoins(8); }, 6900);
    add(() => { setPhase("done"); onDone && onDone(); }, 8300);
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line
  }, []);

  const flying = ["liftoff", "orbit", "done"].includes(phase);
  return (
    <div className="text-center py-2 relative scroll-mt-24" data-testid="rocket-celebration">
      <div ref={sceneRef} className="relative mx-auto w-full max-w-md h-60 overflow-hidden rounded-2xl"
        style={{ background: "linear-gradient(180deg,#020B1C 0%,#0A2342 60%,#123055 100%)" }} data-testid="rocket-scene">
        {/* stars */}
        {[[24, 26], [70, 14], [130, 40], [200, 20], [268, 34], [330, 16], [300, 62], [46, 62]].map(([x, y], i) => (
          <motion.span key={i} className="absolute rounded-full" style={{ left: x, top: y, width: 3, height: 3, background: "#fff" }}
            animate={{ opacity: [0.25, 1, 0.25] }} transition={{ duration: 1.6, delay: i * 0.2, repeat: Infinity }} />
        ))}
        <div className="absolute right-6 top-6 rounded-full" style={{ width: 34, height: 34, background: "#F5F3CE", boxShadow: "0 0 18px rgba(245,243,206,0.7)" }} />
        {/* launch pad */}
        <div className="absolute left-0 right-0" style={{ bottom: 0, height: 14, background: "#06131A" }} />
        <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: 12, width: 110, height: 8, background: "#37474F", borderRadius: 3 }} />

        {/* countdown */}
        <AnimatePresence>
          {phase === "count" && (
            <motion.div key={tick} className="absolute inset-0 flex items-center justify-center font-display font-black select-none pointer-events-none"
              style={{ color: "#CCFF00", fontSize: 84, textShadow: "0 0 30px rgba(204,255,0,0.6)" }}
              initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1.15, opacity: 1 }} exit={{ scale: 1.6, opacity: 0 }} transition={{ duration: 0.45 }}
              data-testid="rocket-countdown">
              {tick}
            </motion.div>
          )}
        </AnimatePresence>

        {/* the rocket */}
        <motion.div className="absolute left-1/2" style={{ marginLeft: -35, bottom: 16 }}
          animate={phase === "ignite" ? { x: [-3, 3, -3, 3, 0], transition: { duration: 0.9 } }
            : flying ? { y: -290, transition: { duration: 2.6, ease: [0.45, 0, 0.8, 1] } } : {}}
          data-testid="rocket-ship">
          <Rocket />
          {/* flame */}
          {(phase === "ignite" || flying) && (
            <motion.div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: -26 }}
              animate={{ scaleY: [0.7, 1.25, 0.8], opacity: [0.8, 1, 0.8] }} transition={{ duration: 0.22, repeat: Infinity }}>
              <svg width="34" height="44" viewBox="0 0 34 44">
                <path d="M17 44 q-14 -14 -8 -30 q4 8 8 8 q4 0 8 -8 q6 16 -8 30z" fill="#FF9E00" />
                <path d="M17 38 q-7 -8 -4 -17 q2 5 4 5 q2 0 4 -5 q3 9 -4 17z" fill="#FFD600" />
              </svg>
            </motion.div>
          )}
        </motion.div>

        {flying && (
          <motion.div className="absolute inset-x-0 bottom-4 text-center font-display font-bold select-none"
            style={{ color: "#7DD3FC", fontSize: 18 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
            data-testid="rocket-orbit-text">
            TO THE MOON 🚀
          </motion.div>
        )}
      </div>

      <div className={`flex flex-col items-center gap-1.5 mt-2 transition-opacity duration-500 ${showScore ? "opacity-100" : "opacity-0"}`}>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }} data-testid="quiz-xp-awarded">
          <span className="text-2xl">🪙</span>
          <span className="text-xl sm:text-2xl leading-none">+{count.toLocaleString()} coins today</span>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }} data-testid="coin-grand-total">
          <span className="text-2xl">🪙</span>
          <span className="text-3xl leading-none text-outline-lime">{total.toLocaleString()}</span>
          <span className="text-xl leading-none">total</span>
        </div>
        {scorePercent != null && <div className="text-3xl font-extrabold font-display text-[#8fb300]" data-testid="quiz-score">{scorePercent}%</div>}
      </div>

      {showScore && <CoinStreakBanner celebrationsEnabled={soundOn} compact className="mt-2 max-w-md mx-auto justify-center text-center" />}

      <AnimatePresence>
        {phase === "done" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid="coin-done" className="scroll-mb-24">
            <p className="text-black font-semibold max-w-md mx-auto mt-2" data-testid="quiz-encouragement">
              Liftoff! Your coins just reached a whole new altitude.
            </p>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {hasPrev && <button data-testid="quiz-prev-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={() => onPrev && onPrev()}>← Prev</button>}
              {onRetry && <button data-testid="quiz-retry-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={onRetry}>Redo</button>}
              {canAdvance && onContinue && <button data-testid="quiz-continue-button" className="btn-primary text-sm px-4 py-2 whitespace-nowrap" onClick={onContinue}>Next →</button>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
